import useWebSocket from "react-use-websocket";

import {
    InputAudioBufferAppendCommand,
    InputAudioBufferClearCommand,
    Message,
    ResponseAudioDelta,
    ResponseAudioTranscriptDelta,
    ResponseDone,
    // SessionUpdateCommand,
    ExtensionMiddleTierToolResponse,
    ResponseInputAudioTranscriptionCompleted,
    ResponseAudioTranscriptDone
} from "@/types";
import axios from "axios";

type Parameters = {
    useDirectAoaiApi?: boolean; // If true, the middle tier will be skipped and the AOAI ws API will be called directly
    aoaiEndpointOverride?: string;
    aoaiApiKeyOverride?: string;
    aoaiModelOverride?: string;

    enableInputAudioTranscription?: boolean;
    onWebSocketOpen?: () => void;
    onWebSocketClose?: () => void;
    onWebSocketError?: (event: Event) => void;
    onWebSocketMessage?: (event: MessageEvent<any>) => void;

    onReceivedResponseAudioDelta?: (message: ResponseAudioDelta) => void;
    onReceivedInputAudioBufferSpeechStarted?: (message: Message) => void;
    onReceivedResponseDone?: (message: ResponseDone) => void;
    onReceivedExtensionMiddleTierToolResponse?: (message: ExtensionMiddleTierToolResponse) => void;
    onReceivedResponseAudioTranscriptDelta?: (message: ResponseAudioTranscriptDelta) => void;
    onReceivedResponseAudioTranscriptDone?: (message: ResponseAudioTranscriptDone) => void;
    onReceivedInputAudioTranscriptionCompleted?: (message: ResponseInputAudioTranscriptionCompleted) => void;
    onReceivedError?: (message: Message) => void;
};

export default function useRealTime({
    useDirectAoaiApi,
    aoaiEndpointOverride,
    aoaiApiKeyOverride,
    aoaiModelOverride,
    // enableInputAudioTranscription,
    onWebSocketOpen,
    onWebSocketClose,
    onWebSocketError,
    onWebSocketMessage,
    onReceivedResponseDone,
    onReceivedResponseAudioDelta,
    onReceivedResponseAudioTranscriptDelta,
    onReceivedResponseAudioTranscriptDone,
    onReceivedInputAudioBufferSpeechStarted,
    onReceivedExtensionMiddleTierToolResponse,
    onReceivedInputAudioTranscriptionCompleted,
    onReceivedError
}: Parameters) {
    const wsEndpoint = useDirectAoaiApi
        ? `${aoaiEndpointOverride}/openai/realtime?api-key=${aoaiApiKeyOverride}&deployment=${aoaiModelOverride}&api-version=2024-10-01-preview`
        : `/realtime`;

    const { sendJsonMessage, getWebSocket } = useWebSocket(wsEndpoint, {
        onOpen: () => onWebSocketOpen?.(),
        onClose: () => onWebSocketClose?.(),
        onError: event => onWebSocketError?.(event),
        onMessage: event => onMessageReceived(event),
        shouldReconnect: () => true
    });

    const startSession = () => {
        return new Promise(async (resolve, reject) => {
            const sessionUpdate = {
                type: "session.update",
                session: {
                    turn_detection: { type: "server_vad" }
                    // input_audio_format: "g711_ulaw",
                    // output_audio_format: "g711_ulaw",
                    // modalities: ["text", "audio"],
                    // temperature: 0.8,
                    // voice: "alloy",
                    // instructions:
                    //     "You are a helpful and bubbly AI assistant who loves to chat about anything the user is interested about and is prepared to offer them facts. You have a penchant for dad jokes, owl jokes, and rickrolling – subtly. Always stay positive, but work in a joke when appropriate."
                }
            };

            console.log("Sending session update:", sessionUpdate);
            sendJsonMessage(sessionUpdate);

            // Uncomment the following line to have AI speak first:
            sendInitialConversationItem();
            try {
                const url = import.meta.env.VITE_CALL_INSIGHTS_BACKEND + "/app/lisa/start-conv";
                const response = await axios.post(url);
                console.log(response.data);
                resolve(response.data.conv_id);
            } catch (err) {
                reject(err);
            }
        });
    };
    // Send initial conversation item if AI talks first
    const sendInitialConversationItem = () => {
        console.log("sendInitialConv called");
        const initialConversationItem = {
            type: "conversation.item.create",
            item: {
                type: "message",
                role: "user",
                content: [
                    {
                        type: "input_text",
                        text: 'Greet the user with "Hello there!"'
                    }
                ]
            }
        };

        sendJsonMessage(initialConversationItem);
        sendJsonMessage({ type: "response.create" });
    };

    // const startSession_old = () => {
    //     const command: SessionUpdateCommand = {
    //         type: "session.update",
    //         session: {
    //             turn_detection: {
    //                 type: "server_vad"
    //             }
    //         }
    //     };

    //     if (enableInputAudioTranscription) {
    //         command.session.input_audio_transcription = {
    //             model: "whisper-1"
    //         };
    //     }

    //     sendJsonMessage(command);
    //     const command2 = {
    //         type: "conversation.item.create",
    //         item: {
    //             type: "message",
    //             role: "user",
    //             content: [
    //                 {
    //                     type: "input_text",
    //                     text: 'Greet the user with "Hello there!"'
    //                 }
    //             ]
    //         }
    //     };
    //     sendJsonMessage(command2);
    // };

    const addUserAudio = (base64Audio: string) => {
        const command: InputAudioBufferAppendCommand = {
            type: "input_audio_buffer.append",
            audio: base64Audio
        };

        sendJsonMessage(command);
    };

    const inputAudioBufferClear = () => {
        const command: InputAudioBufferClearCommand = {
            type: "input_audio_buffer.clear"
        };

        sendJsonMessage(command);
    };

    const onMessageReceived = (event: MessageEvent<any>) => {
        onWebSocketMessage?.(event);

        let message: Message;
        try {
            message = JSON.parse(event.data);
        } catch (e) {
            console.error("Failed to parse JSON message:", e);
            throw e;
        }

        switch (message.type) {
            case "response.done":
                onReceivedResponseDone?.(message as ResponseDone);
                break;
            case "response.audio.delta":
                onReceivedResponseAudioDelta?.(message as ResponseAudioDelta);
                break;
            case "response.audio_transcript.delta":
                onReceivedResponseAudioTranscriptDelta?.(message as ResponseAudioTranscriptDelta);
                break;
            case "response.audio_transcript.done":
                onReceivedResponseAudioTranscriptDone?.(message as ResponseAudioTranscriptDone);
                break;
            case "input_audio_buffer.speech_started":
                onReceivedInputAudioBufferSpeechStarted?.(message);
                break;
            case "conversation.item.input_audio_transcription.completed":
                onReceivedInputAudioTranscriptionCompleted?.(message as ResponseInputAudioTranscriptionCompleted);
                break;
            case "extension.middle_tier_tool_response":
                onReceivedExtensionMiddleTierToolResponse?.(message as ExtensionMiddleTierToolResponse);
                break;
            case "error":
                onReceivedError?.(message);
                break;
        }
    };
    const endSession = async (convId: number, callDuration: number) => {
        const websocket = getWebSocket();
        if (websocket) {
            websocket.close(); // Close the WebSocket connection
        }
        if (convId) {
            try {
                const url = import.meta.env.VITE_CALL_INSIGHTS_BACKEND + "/app/lisa/end-conv";
                await axios.post(url, { convId, callDuration });
            } catch (err) {
                console.log(err);
            }
        }
    };

    return { startSession, addUserAudio, inputAudioBufferClear, endSession };
}
